import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { authService } from '../../lib/auth-service';
import { useRoleAwareLayout } from '../../hooks/responsive/useResponsive';
import { Home, Landmark, Calendar, MessageSquare, Settings, ArrowLeft, CheckSquare, ShoppingCart, SquareCheckBig, Luggage } from 'lucide-react';

const TABS = [
  // Dashboard
  {
    key: 'home',
    label: 'Dashboard',
    path: null, // will be set dynamically below
    icon: <Home className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    color: 'slate'
  },
  // Tasks (Personal Organization Hub)
  {
    key: 'tasks',
    label: 'Tasks',
    path: '/tasks',
    icon: <SquareCheckBig className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    color: 'azure'
  },

  // Family (Family Activities, Calendar, Planning)
  {
    key: 'family',
    label: 'Family',
    path: '/family',
    icon: <Calendar className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    color: 'gold'
  },

  // Shopping
  {
    key: 'shopping',
    label: 'Shopping',
    path: '/shopping',
    icon: <ShoppingCart className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    color: 'coral'
  },

  // Packing
  {
    key: 'packing',
    label: 'Packing',
    path: '/packing',
    icon: <Luggage className="w-5 h-5" />,
    roles: ['parent', 'kid', 'both'],
    color: 'violet'
  }
];

export default function TabNav() {
  const { currentUser, setCurrentUser, family } = useFamilyContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isParent = currentUser?.isParent || currentUser?.originalParent;
  const isViewingAsKid = !currentUser?.isParent && currentUser?.originalParent;
  const role = isViewingAsKid ? 'kid' : (isParent ? 'parent' : 'kid');
  const tabs = [...TABS];
  
  // Use unified responsive system
  useRoleAwareLayout();
  
  // Note: This is the simplified 4-tab mobile navigation
  // SideNav maintains full detailed navigation for desktop/tablet

  const returnToParentView = async () => {
    const user = await authService.getUser();
    if (!user) return;

    // Clear cached data to avoid stale data
    localStorage.removeItem(`family-data-${user.id}`);
    localStorage.removeItem(`family-data-timestamp-${user.id}`);

    // Find the actual logged-in parent
    const loggedInParent = family?.parents?.find(p => p.authUserId === user?.id);
    if (loggedInParent) {
      setCurrentUser({
        ...loggedInParent,
        isParent: true,
        originalParent: false
      });
      navigate('/parent-dashboard');
    }
  };

  // Set dynamic paths based on role and viewport - using modular architecture
  const tabsWithDynamicPaths = tabs.map(tab => {
    if (tab.key === 'home') {
      // Use new modular dashboard architecture
      const homePath = '/dashboard';
      
      return {
        ...tab,
        path: homePath
      };
    }
    
    if (tab.key === 'family') {
      // Use the unified events page with tabs
      return {
        ...tab,
        path: '/family'
      };
    }

    if (tab.key === 'shopping') {
      return {
        ...tab,
        path: '/shopping'
      };
    }
    

    return tab;
  });

  const visibleTabs = tabsWithDynamicPaths.filter(tab => tab.roles.includes(role));

  // Helper function to determine if a tab should be active
  const isTabActive = (tab) => {
    const currentPath = location.pathname;
    
    // Direct path match
    if (currentPath === tab.path) return true;
    
    // Category-based matching for grouped tabs
    switch (tab.key) {
      case 'home':
        return currentPath === '/parent-dashboard' || currentPath === '/dashboard' || currentPath === '/mobile-home';
      
      case 'family':
        return currentPath.startsWith('/family') ||
               currentPath.startsWith('/calendar') ||
               currentPath.startsWith('/family') || 
               currentPath.startsWith('/family-calendar') ||
               currentPath.startsWith('/family-time') ||
               currentPath === '/family' ||
               (isParent && currentPath.startsWith('/planning')) ||
               (!isParent && currentPath.startsWith('/plan')) ||
               (!isParent && currentPath.startsWith('/activities'));
      
      case 'shopping':
        return currentPath.startsWith('/shopping');


      
      case 'tasks':
        return currentPath.startsWith('/tasks');
      
      case 'manage':
        return currentPath.startsWith('/manage') ||
               currentPath.startsWith('/shopping') || 
               currentPath.startsWith('/ideas-opportunities') ||
               currentPath.startsWith('/settings') ||
               currentPath.startsWith('/profile-management') ||
               currentPath.startsWith('/sms-settings');
      
      default:
        return false;
    }
  };

  return (
    <nav
      className="nav-main"
      aria-label="Main navigation"
    >
      <div className="flex items-center justify-between px-3 py-3 sm:px-2 sm:py-2">
        <ul className="flex flex-nowrap gap-1 sm:gap-2 flex-1 justify-around sm:justify-center">
          {visibleTabs.map(tab => (
            <li key={tab.key} className="flex-shrink-0">
              <NavLink
                to={tab.path}
                className={() => {
                  const isActive = isTabActive(tab);
                  const colorClasses = {
                    slate: isActive ? 'bg-slate-900 text-white' : 'bg-stone-100 text-stone-700 hover:bg-slate-50 hover:text-slate-900',
                    azure: isActive ? 'bg-azure-500 text-white' : 'bg-stone-100 text-stone-700 hover:bg-azure-50 hover:text-azure-900',
                    magenta: isActive ? 'bg-magenta-500 text-white' : 'bg-stone-100 text-stone-700 hover:bg-magenta-50 hover:text-magenta-900',
                    gold: isActive ? 'bg-gold-500 text-white' : 'bg-stone-100 text-stone-700 hover:bg-gold-50 hover:text-gold-900',
                    teal: isActive ? 'bg-teal-500 text-white' : 'bg-stone-100 text-stone-700 hover:bg-teal-50 hover:text-teal-900',
                    coral: isActive ? 'bg-coral-500 text-white' : 'bg-stone-100 text-stone-700 hover:bg-coral-50 hover:text-coral-900'
                  };
                  
                  return `nav-tab-item ${colorClasses[tab.color] || colorClasses.slate} ${isActive ? 'shadow' : ''}`;
                }}
                aria-current={isTabActive(tab) ? 'page' : undefined}
              >
                <div className="relative">
                  {tab.icon}
                </div>
                <span className="hidden sm:inline">{tab.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
        
        {/* Return to Parent Dashboard button */}
        {currentUser?.originalParent && (
          <button
            onClick={returnToParentView}
            className="btn-parent-return-nav"
            title="Return to Parent Dashboard"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Parent View</span>
          </button>
        )}
      </div>
    </nav>
  );
} 